import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type RefObject } from 'react';

/** Idle timeout before an expanded map overlay (legend / zoom panel) auto-collapses. */
export const MAP_OVERLAY_IDLE_COLLAPSE_MS = 30_000;

/** Marks legend / zoom panels so outside-tap collapse ignores sibling overlays. */
export const MAP_OVERLAY_ROOT_ATTR = 'data-map-overlay';

/** Minimal Mapbox surface used to collapse overlays on drag start. */
export type MapOverlayMapLike = {
  on: (type: string, listener: (...args: any[]) => void) => void;
  off: (type: string, listener: (...args: any[]) => void) => void;
};

export type UseMapOverlayExpandCollapseOptions = {
  map?: MapOverlayMapLike | null;
  idleMs?: number;
  collapsedAriaLabel: string;
  expandedAriaLabel: string;
};

export type MapOverlayRootProps = {
  role: 'button' | 'group';
  tabIndex?: number;
  'aria-expanded': boolean;
  'aria-label': string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
};

/**
 * Shared tap-to-grow / outside-collapse / idle-collapse behavior for map overlays
 * (heat legend, zoom/pin panel). Each caller gets an independent expanded state.
 */
export function useMapOverlayExpandCollapse({
  map = null,
  idleMs = MAP_OVERLAY_IDLE_COLLAPSE_MS,
  collapsedAriaLabel,
  expandedAriaLabel,
}: UseMapOverlayExpandCollapseOptions): {
  expanded: boolean;
  rootRef: RefObject<HTMLElement | null>;
  expand: () => void;
  collapse: () => void;
  markInteraction: () => void;
  handleCollapsedActivate: (event: MouseEvent | KeyboardEvent) => void;
  rootProps: MapOverlayRootProps;
} {
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef<HTMLElement | null>(null);
  const idleTimerRef = useRef<number | null>(null);

  const clearIdleTimer = () => {
    if (idleTimerRef.current != null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const startIdleTimer = () => {
    clearIdleTimer();
    idleTimerRef.current = window.setTimeout(() => {
      idleTimerRef.current = null;
      setExpanded(false);
    }, idleMs);
  };

  const expand = () => {
    setExpanded(true);
  };

  const collapse = () => {
    setExpanded(false);
  };

  /** Reset the inactivity timer while expanded (sun/tile/zoom/pin taps). */
  const markInteraction = () => {
    if (expanded) {
      startIdleTimer();
    }
  };

  // Start / reset the idle timer while expanded; clear on collapse/unmount.
  useEffect(() => {
    if (!expanded) {
      clearIdleTimer();
      return;
    }
    startIdleTimer();
    return clearIdleTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-arm only when expanded flips
  }, [expanded, idleMs]);

  // Outside pointerdown collapses but does not stop the event, so the target
  // (map, chips, drawer) still receives its normal action. Sibling map overlays
  // (legend ↔ zoom/pin) are ignored so both can stay expanded independently.
  useEffect(() => {
    if (!expanded) return;
    const onPointerDown = (event: PointerEvent) => {
      const root = rootRef.current;
      const target = event.target as Node | null;
      if (!root || !target || root.contains(target)) return;
      if (target instanceof Element && target.closest(`[${MAP_OVERLAY_ROOT_ATTR}]`)) {
        return;
      }
      collapse();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [expanded]);

  // Collapse when the user starts dragging the map.
  useEffect(() => {
    if (!expanded || !map) return;
    const onDragStart = () => collapse();
    map.on('dragstart', onDragStart);
    return () => {
      map.off('dragstart', onDragStart);
    };
  }, [expanded, map]);

  const handleCollapsedActivate = (event: MouseEvent | KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    expand();
  };

  const rootProps: MapOverlayRootProps = {
    role: expanded ? 'group' : 'button',
    tabIndex: expanded ? undefined : 0,
    'aria-expanded': expanded,
    'aria-label': expanded ? expandedAriaLabel : collapsedAriaLabel,
    onClick: expanded ? undefined : handleCollapsedActivate,
    onKeyDown: expanded
      ? undefined
      : (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleCollapsedActivate(event);
          }
        },
  };

  return {
    expanded,
    rootRef,
    expand,
    collapse,
    markInteraction,
    handleCollapsedActivate,
    rootProps,
  };
}
